import { AztecAddress } from '@aztec/aztec.js/addresses';
import { getPublicEvents } from '@aztec/aztec.js/events';
import { createAztecNodeClient } from '@aztec/aztec.js/node';
import { TxHash } from '@aztec/aztec.js/tx';
import { bytesToHex, normalizePayoutCurveData } from '@train-protocol/sdk';
import { TrainContract } from '../artifacts/Train';
export function requireSigner(signer) {
    if (!signer)
        throw new Error('Signer required');
    return signer;
}
export function getNode(rpcUrl, cachedNode) {
    if (cachedNode)
        return cachedNode;
    return createAztecNodeClient(rpcUrl);
}
const contractRegistrations = new WeakMap();
function isLegacyRegisterContractReturn(error) {
    if (typeof error !== 'object' || error === null ||
        error.name !== 'ZodError')
        return false;
    const issues = error.issues;
    return Array.isArray(issues) && issues.length > 0 && issues.every(issue => issue.code === 'invalid_type' &&
        issue.expected === 'void' &&
        Array.isArray(issue.path) &&
        issue.path.length === 0);
}
/**
 * Aztec v5 changed registerContract's return type from the registered instance
 * to void. Older extension wallets still return the instance after successfully
 * registering it, which the v5 wallet client rejects during response validation.
 *
 * Some extension wallets also validate the provided artifact against the
 * instance's current class id ("Contract artifact doesn't match instance's
 * current class id"). The artifact parameter is optional in the wallet RPC —
 * the wallet resolves the class from its own storage or the chain — so on any
 * other registration failure we retry with the instance alone before giving up.
 */
export async function registerContractCompat(wallet, ...args) {
    const [instance, artifact] = args;
    const address = instance.address?.toString();
    const registrations = contractRegistrations.get(wallet) ?? new Map();
    if (!contractRegistrations.has(wallet))
        contractRegistrations.set(wallet, registrations);
    if (address) {
        const existing = registrations.get(address);
        if (existing)
            return existing;
    }
    const registration = (async () => {
        try {
            await wallet.registerContract(...args);
        }
        catch (error) {
            if (isLegacyRegisterContractReturn(error))
                return;
            if (!artifact)
                throw error;
            console.warn(`[registerContractCompat] artifact registration failed for ${address}; ` +
                'retrying instance-only (wallet will lack the artifact for this class):', error);
            try {
                await wallet.registerContract(instance);
            }
            catch (retryError) {
                if (!isLegacyRegisterContractReturn(retryError))
                    throw error;
            }
        }
    })();
    if (address)
        registrations.set(address, registration);
    try {
        await registration;
    }
    catch (error) {
        if (address)
            registrations.delete(address);
        throw error;
    }
}
export async function getContractInstance(contractAddress, signer, nodeOrUrl) {
    const aztecAtomicContract = AztecAddress.fromStringUnsafe(contractAddress);
    const node = typeof nodeOrUrl === 'string' ? createAztecNodeClient(nodeOrUrl) : nodeOrUrl;
    const trainInstance = await node.getContract(aztecAtomicContract);
    if (!trainInstance)
        throw new Error('Train contract not found');
    await registerContractCompat(signer.wallet, trainInstance, TrainContract.artifact);
    const contract = TrainContract.at(aztecAtomicContract, signer.wallet);
    const userAztecAddress = AztecAddress.fromStringUnsafe(signer.address);
    return { contract, userAztecAddress, node };
}
export function parseSecret(rawSecret) {
    const secretBytes = Array.from(rawSecret || []);
    const secretHex = secretBytes.length > 0 ? bytesToHex(secretBytes) : '0x0';
    return BigInt(secretHex);
}
export function strToBytes(str, length) {
    const bytes = new TextEncoder().encode(str);
    const result = new Array(length).fill(0);
    for (let i = 0; i < Math.min(bytes.length, length); i++) {
        result[i] = bytes[i];
    }
    return result;
}
/**
 * Decode the quote's payout-curve config into the contract's fixed-width `[u8; length]`
 * field, zero-padded on the right. Absent config is all zeros — the same value the
 * contract read before the field carried anything.
 *
 * Note the two deliberate differences from `strToBytes`: the input is hex rather than
 * text (validated by the shared normalizer, so malformed hex fails closed), and an
 * oversized value throws instead of truncating. A silently shortened curve config would
 * change the payout the user agreed to.
 */
export function payoutCurveDataToBytes(value, length) {
    const hex = normalizePayoutCurveData(value ?? '0x').slice(2);
    const byteLength = hex.length / 2;
    if (byteLength > length) {
        throw new Error(`Payout curve data must be at most ${length} bytes, got ${byteLength}`);
    }
    const result = new Array(length).fill(0);
    for (let i = 0; i < byteLength; i++) {
        result[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
    }
    return result;
}
export async function findEventDataFromLogs(node, txHash, contractAddress, hashlock) {
    try {
        const { events } = await getPublicEvents(node, TrainContract.events.UserLocked, {
            contractAddress: AztecAddress.fromStringUnsafe(contractAddress),
            txHash: TxHash.fromString(txHash),
        });
        const aztecBytesToString = (bytes) => new TextDecoder().decode(new Uint8Array(bytes.map(Number))).replace(/\0/g, '').trim();
        for (const { event: decoded } of events) {
            const decodedHashlock = bytesToHex(Array.from(decoded.hashlock).map(Number));
            if (decodedHashlock.toLowerCase() !== hashlock.toLowerCase())
                continue;
            const data = {};
            if (decoded.dst_chain)
                data.dstChain = aztecBytesToString(decoded.dst_chain);
            if (decoded.dst_address)
                data.dstAddress = aztecBytesToString(decoded.dst_address);
            if (decoded.dst_amount != null)
                data.dstAmount = BigInt(decoded.dst_amount);
            if (decoded.dst_token)
                data.dstToken = aztecBytesToString(decoded.dst_token);
            if (decoded.user_data) {
                data.userData = aztecBytesToString(decoded.user_data) || undefined;
            }
            if (decoded.solver_data) {
                data.solverData = aztecBytesToString(decoded.solver_data) || undefined;
            }
            // The reward recipient is the solver's address on the *destination* chain — it is a
            // cross-chain identifier (byte string, not AztecAddress) for that reason. It is the
            // only place a swap recovered from a source tx can learn which solver to read the
            // destination lock from, since the quote that carried it is gone.
            if (decoded.reward_recipient) {
                data.rewardRecipient = aztecBytesToString(decoded.reward_recipient) || undefined;
            }
            return data;
        }
    }
    catch (e) {
        console.error('Error fetching event data from Aztec logs:', e);
    }
    return {};
}
